import React, { useState, useMemo, useRef, useEffect } from 'react';
import type { InventoryItem } from '../../types';
import { useSoftKeyboardField } from '../../hooks/useSoftKeyboardField';
import MagnifyingGlassIcon from '../icons/MagnifyingGlassIcon';
import CheckCircleIcon from '../icons/CheckCircleIcon';
import XCircleIcon from '../icons/XCircleIcon';

// Separate component for input to use hooks properly
const StockTakeInput: React.FC<{
  itemId: number;
  itemName: string;
  value: string;
  onChange: (value: string) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onFocus: () => void;
  inputRef: (el: HTMLInputElement | null) => void;
}> = ({ itemId, itemName, value, onChange, onKeyDown, onFocus, inputRef }) => {
  const countField = useSoftKeyboardField({
    id: `stock-take-${itemId}`,
    type: 'numeric',
    value: value,
    label: `Physical Count for ${itemName}`,
    onChange: onChange,
  });

  // Handle both soft keyboard and direct input
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    onFocus();
    if (countField.onFocus) {
      countField.onFocus(e);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Always allow direct input to work
    const newValue = e.target.value;
    onChange(newValue);
    // Also call the soft keyboard handler if it exists
    if (countField.onChange) {
      countField.onChange(e);
    }
  };

  // Ensure value is always a string, never undefined
  const safeValue = value === undefined || value === null ? '' : String(value);

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="decimal"
      value={safeValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onKeyDown={onKeyDown}
      readOnly={countField.readOnly}
      className="w-32 px-3 py-2 text-lg border-2 border-gray-300 rounded-lg shadow-sm bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
      placeholder="0.00"
    />
  );
};

interface StockTakeProps {
  inventoryItems: InventoryItem[];
  onStockTake: (physicalCounts: { [itemId: number]: number }) => void;
}

const StockTake: React.FC<StockTakeProps> = ({ inventoryItems, onStockTake }) => {
  const [physicalCounts, setPhysicalCounts] = useState<{ [key: number]: number | '' }>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [showOnlyVariances, setShowOnlyVariances] = useState(false);
  const [sortBy, setSortBy] = useState<'name' | 'variance' | 'stock'>('name');
  const inputRefs = useRef<{ [key: number]: HTMLInputElement | null }>({});
  const [focusedItemId, setFocusedItemId] = useState<number | null>(null);

  // Get unique categories
  const categories = useMemo(() => {
    const cats = new Set<string>();
    inventoryItems.forEach(item => {
      if (item.category) cats.add(item.category);
    });
    return Array.from(cats).sort();
  }, [inventoryItems]);

  // Filter and sort items
  const filteredItems = useMemo(() => {
    let filtered = inventoryItems.filter(item => {
      // Search filter
      if (searchTerm && !item.name.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      // Category filter
      if (filterCategory !== 'all' && item.category !== filterCategory) {
        return false;
      }
      // Variance filter
      if (showOnlyVariances) {
        const physicalCount = physicalCounts[item.id];
        const variance = typeof physicalCount === 'number' ? physicalCount - item.stock : null;
        if (variance === null || variance === 0) return false;
      }
      return true;
    });

    // Sort
    filtered = [...filtered].sort((a, b) => {
      if (sortBy === 'name') {
        return a.name.localeCompare(b.name);
      } else if (sortBy === 'variance') {
        const varianceA = typeof physicalCounts[a.id] === 'number' 
          ? (physicalCounts[a.id] as number) - a.stock 
          : 0;
        const varianceB = typeof physicalCounts[b.id] === 'number' 
          ? (physicalCounts[b.id] as number) - b.stock 
          : 0;
        return Math.abs(varianceB) - Math.abs(varianceA);
      } else {
        return b.stock - a.stock; // Sort by stock descending
      }
    });

    return filtered;
  }, [inventoryItems, searchTerm, filterCategory, showOnlyVariances, sortBy, physicalCounts]);

  // Calculate progress
  const progress = useMemo(() => {
    const total = inventoryItems.length;
    const counted = Object.keys(physicalCounts).filter(
      key => typeof physicalCounts[Number(key)] === 'number'
    ).length;
    return { counted, total, percentage: total > 0 ? Math.round((counted / total) * 100) : 0 };
  }, [inventoryItems.length, physicalCounts]);

  // Calculate summary statistics
  const summary = useMemo(() => {
    let totalVariance = 0;
    let itemsWithVariance = 0;
    let itemsOver = 0;
    let itemsUnder = 0;

    Object.keys(physicalCounts).forEach(key => {
      const itemId = Number(key);
      const count = physicalCounts[itemId];
      if (typeof count === 'number') {
        const item = inventoryItems.find(i => i.id === itemId);
        if (item) {
          const variance = count - item.stock;
          totalVariance += variance;
          if (variance !== 0) {
            itemsWithVariance++;
            if (variance > 0) itemsOver++;
            else itemsUnder++;
          }
        }
      }
    });

    return { totalVariance, itemsWithVariance, itemsOver, itemsUnder };
  }, [physicalCounts, inventoryItems]);

  const handleCountChange = (itemId: number, value: string) => {
    setPhysicalCounts(prev => ({
      ...prev,
      [itemId]: value === '' ? '' : (isNaN(parseFloat(value)) ? '' : parseFloat(value)),
    }));
  };

  const handleMatchSystemStock = (item: InventoryItem) => {
    setPhysicalCounts(prev => ({
      ...prev,
      [item.id]: item.stock,
    }));
    // Focus next input
    const currentIndex = filteredItems.findIndex(i => i.id === item.id);
    if (currentIndex < filteredItems.length - 1) {
      const nextItem = filteredItems[currentIndex + 1];
      setTimeout(() => {
        inputRefs.current[nextItem.id]?.focus();
      }, 100);
    }
  };

  const handleMatchAll = () => {
    const allCounts: { [key: number]: number | '' } = {};
    inventoryItems.forEach(item => {
      allCounts[item.id] = item.stock;
    });
    setPhysicalCounts(allCounts);
  };

  const handleClearAll = () => {
    if (window.confirm('Clear all entered counts?')) {
      setPhysicalCounts({});
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, itemId: number, currentIndex: number) => {
    if (e.key === 'Enter' || e.key === 'ArrowDown') {
      e.preventDefault();
      if (currentIndex < filteredItems.length - 1) {
        const nextItem = filteredItems[currentIndex + 1];
        inputRefs.current[nextItem.id]?.focus();
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (currentIndex > 0) {
        const prevItem = filteredItems[currentIndex - 1];
        inputRefs.current[prevItem.id]?.focus();
      }
    }
  };

  const handleFinalize = () => {
    const finalCounts: { [itemId: number]: number } = {};
    for (const itemId in physicalCounts) {
      const count = physicalCounts[itemId];
      if (typeof count === 'number' && !isNaN(count) && count >= 0) {
        finalCounts[Number(itemId)] = count;
      }
    }

    if (Object.keys(finalCounts).length === 0) {
      alert('Please enter at least one physical count before finalizing.');
      return;
    }

    const uncounted = inventoryItems.length - Object.keys(finalCounts).length;
    let confirmMessage = `Finalize stock take for ${Object.keys(finalCounts).length} item(s)?`;
    if (uncounted > 0) {
      confirmMessage += `\n\n${uncounted} item(s) will not be updated.`;
    }

    if (window.confirm(confirmMessage)) {
      onStockTake(finalCounts);
      setPhysicalCounts({});
      setSearchTerm('');
      setShowOnlyVariances(false);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg">
      {/* Header with Progress */}
      <div className="p-6 border-b bg-gradient-to-r from-pink-50 to-blue-50">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Daily Stock Take</h2>
            <p className="text-sm text-gray-600 mt-1">Enter physical counts to update system stock levels</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-pink-600">{progress.percentage}%</div>
            <div className="text-sm text-gray-600">
              {progress.counted} of {progress.total} items counted
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
          <div
            className="bg-pink-500 h-3 rounded-full transition-all duration-300"
            style={{ width: `${progress.percentage}%` }}
          />
        </div>

        {/* Filters and Actions */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
            />
          </div>

          {/* Category Filter */}
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
          >
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'name' | 'variance' | 'stock')}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
          >
            <option value="name">Sort by Name</option>
            <option value="variance">Sort by Variance</option>
            <option value="stock">Sort by Stock (High to Low)</option>
          </select>

          {/* Quick Actions */}
          <div className="flex gap-2">
            <button
              onClick={handleMatchAll}
              className="px-3 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              title="Match all items to system stock"
            >
              Match All
            </button>
            <button
              onClick={handleClearAll}
              className="px-3 py-2 text-sm bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Clear All
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        {summary.itemsWithVariance > 0 && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Total Variance:</span>
                <span className={`ml-2 font-semibold ${summary.totalVariance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {summary.totalVariance >= 0 ? '+' : ''}{summary.totalVariance.toFixed(2)}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Items with Variance:</span>
                <span className="ml-2 font-semibold text-orange-600">{summary.itemsWithVariance}</span>
              </div>
              <div>
                <span className="text-gray-600">Over Count:</span>
                <span className="ml-2 font-semibold text-green-600">{summary.itemsOver}</span>
              </div>
              <div>
                <span className="text-gray-600">Under Count:</span>
                <span className="ml-2 font-semibold text-red-600">{summary.itemsUnder}</span>
              </div>
            </div>
          </div>
        )}

        {/* Show Only Variances Toggle */}
        <div className="mt-3 flex items-center">
          <input
            type="checkbox"
            id="showVariances"
            checked={showOnlyVariances}
            onChange={(e) => setShowOnlyVariances(e.target.checked)}
            className="mr-2"
          />
          <label htmlFor="showVariances" className="text-sm text-gray-700">
            Show only items with variances
          </label>
        </div>
      </div>

      {/* Items Table */}
      <div className="overflow-x-auto max-h-[calc(100vh-400px)] overflow-y-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
                Item Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                System Stock
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                Physical Count
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                Variance
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                Quick Action
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredItems.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                  No items found. Try adjusting your filters.
                </td>
              </tr>
            ) : (
              filteredItems.map((item, index) => {
                const physicalCount = physicalCounts[item.id];
                const variance = typeof physicalCount === 'number' ? physicalCount - item.stock : null;
                const isComplete = typeof physicalCount === 'number';
                const hasVariance = variance !== null && variance !== 0;

                let varianceColor = 'text-gray-800';
                let varianceBg = 'bg-gray-50';
                if (variance !== null) {
                  if (variance < 0) {
                    varianceColor = 'text-red-700';
                    varianceBg = 'bg-red-50';
                  } else if (variance > 0) {
                    varianceColor = 'text-green-700';
                    varianceBg = 'bg-green-50';
                  } else {
                    varianceColor = 'text-gray-600';
                    varianceBg = 'bg-gray-50';
                  }
                }

                return (
                  <tr
                    key={item.id}
                    className={`${hasVariance ? 'bg-yellow-50' : ''} ${isComplete ? 'opacity-100' : 'opacity-90'} hover:bg-gray-50 transition-colors`}
                  >
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">{item.name}</div>
                          <div className="text-xs text-gray-500">{item.unit}</div>
                          {item.category && (
                            <div className="text-xs text-blue-600 mt-1">{item.category}</div>
                          )}
                        </div>
                        {isComplete && !hasVariance && (
                          <CheckCircleIcon className="w-5 h-5 text-green-500" />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-700 font-medium">
                        {item.stock.toFixed(2)} {item.unit}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <StockTakeInput
                        itemId={item.id}
                        itemName={item.name}
                        value={physicalCount === undefined || physicalCount === null ? '' : (physicalCount === '' ? '' : String(physicalCount))}
                        onChange={(val) => handleCountChange(item.id, val)}
                        onKeyDown={(e) => handleKeyDown(e, item.id, index)}
                        onFocus={() => setFocusedItemId(item.id)}
                        inputRef={(el) => {
                          inputRefs.current[item.id] = el;
                        }}
                      />
                    </td>
                    <td className={`px-4 py-4 whitespace-nowrap ${varianceBg}`}>
                      {variance !== null ? (
                        <div className={`text-sm font-bold ${varianceColor}`}>
                          {variance >= 0 ? '+' : ''}{variance.toFixed(2)} {item.unit}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-400">-</div>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => handleMatchSystemStock(item)}
                        className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                        title="Match to system stock"
                      >
                        Match
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Footer with Finalize Button */}
      <div className="p-6 bg-gray-50 border-t flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {progress.counted > 0 && (
            <>
              <span className="font-semibold">{progress.counted}</span> items counted
              {summary.itemsWithVariance > 0 && (
                <> • <span className="text-orange-600 font-semibold">{summary.itemsWithVariance}</span> with variances</>
              )}
            </>
          )}
        </div>
        <button
          onClick={handleFinalize}
          disabled={progress.counted === 0}
          className="bg-green-500 text-white font-bold py-3 px-8 rounded-lg hover:bg-green-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed text-lg"
        >
          Finalize Stock Take
        </button>
      </div>
    </div>
  );
};

export default StockTake;
